import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const workshops = [
  {
    date: 'Mar 15',
    year: '2025',
    title: 'Advanced Problem Recognition Seminar',
    duration: '3 hours',
    format: 'In-Person',
    location: 'Austin Convention Center',
    description: 'Master the art of identifying problems that others overlook. Learn Dr. Thornwell\'s proprietary 47-point assessment methodology.',
    spots: '12 spots remaining',
    level: 'Advanced'
  },
  {
    date: 'Mar 22',
    year: '2025',
    title: 'Strategic Escalation Workshop',
    duration: '4 hours',
    format: 'Hybrid',
    location: 'Virtual + Austin Office',
    description: 'Discover how to escalate problems effectively across organizational hierarchies. Includes live escalation simulations.',
    spots: '8 spots remaining',
    level: 'Intermediate'
  },
  {
    date: 'Apr 5',
    year: '2025',
    title: 'Problem Documentation Masterclass',
    duration: '6 hours',
    format: 'In-Person',
    location: 'Austin Convention Center',
    description: 'Learn enterprise-grade documentation techniques. Participants will document a real problem from identification through archival.',
    spots: '15 spots remaining',
    level: 'All Levels'
  },
  {
    date: 'Apr 12',
    year: '2025',
    title: 'Mindful Problem Awareness Retreat',
    duration: 'Full Day',
    format: 'In-Person',
    location: 'Hill Country Retreat Center',
    description: 'Immersive day-long experience focused on cultivating perpetual problem awareness through meditation and contemplation.',
    spots: '6 spots remaining',
    level: 'Beginner'
  },
  {
    date: 'Apr 19',
    year: '2025',
    title: 'Crisis Amplification Techniques',
    duration: '2 hours',
    format: 'Virtual',
    location: 'Online Webinar',
    description: 'Learn how to maximize the visibility and impact of identified problems. Includes case studies from Fortune 500 escalations.',
    spots: '20 spots remaining',
    level: 'Advanced'
  },
  {
    date: 'May 3',
    year: '2025',
    title: 'Legacy Problem Planning Summit',
    duration: '5 hours',
    format: 'In-Person',
    location: 'Austin Convention Center',
    description: 'Ensure your problems are preserved for future generations. Estate planning meets problem management in this unique workshop.',
    spots: '10 spots remaining',
    level: 'Executive'
  }
];

const WorkshopCalendar = () => {
  return (
    <section className="relative py-32 bg-gradient-to-b from-black via-zinc-950 to-black">
      {/* Background Image */}
      <div className="absolute inset-0 opacity-5">
        <img
          src="https://public.readdy.ai/ai/img_res/c0e4c7e9d7325e365ddfe16422fce8bc.jpg"
          alt="Peaceful landscape"
          width={1920}
          height={1080}
          className="w-full h-full object-cover"
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div className="text-center mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex items-center justify-center gap-3 mb-6"
          >
            <div className="w-12 h-[2px] bg-gold"></div>
            <span className="text-gold text-sm font-semibold tracking-widest uppercase">
              Educational Programs
            </span>
            <div className="w-12 h-[2px] bg-gold"></div>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-5xl md:text-6xl font-black text-white mb-6"
          >
            Upcoming <span className="text-gold">Workshops</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-xl text-cream/70 max-w-3xl mx-auto"
          >
            Deepen your problem management expertise through our comprehensive workshop series. 
            Each session is led by certified problem awareness specialists.
          </motion.p>
        </div>

        {/* Workshops Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {workshops.map((workshop, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="group bg-gradient-to-br from-zinc-900 to-black border border-gold/20 rounded-2xl overflow-hidden hover:border-gold/50 transition-all duration-500"
            >
              <div className="p-8">
                {/* Date Badge */}
                <div className="flex items-start justify-between mb-6">
                  <div className="bg-gold/10 border border-gold/30 rounded-xl px-6 py-4">
                    <div className="text-3xl font-black text-gold">{workshop.date}</div>
                    <div className="text-cream/60 text-sm">{workshop.year}</div>
                  </div>
                  <div className="bg-gold/5 border border-gold/20 rounded-full px-4 py-2">
                    <span className="text-gold text-xs font-semibold">{workshop.level}</span>
                  </div>
                </div>

                {/* Content */}
                <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-gold transition-colors duration-300">
                  {workshop.title}
                </h3>
                <p className="text-cream/70 mb-6 leading-relaxed text-sm">
                  {workshop.description}
                </p>

                {/* Details */}
                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-3 text-cream/60 text-sm">
                    <i className="ri-time-line text-gold"></i>
                    <span>{workshop.duration}</span>
                  </div>
                  <div className="flex items-center gap-3 text-cream/60 text-sm">
                    <i className="ri-computer-line text-gold"></i>
                    <span>{workshop.format}</span>
                  </div>
                  <div className="flex items-center gap-3 text-cream/60 text-sm">
                    <i className="ri-map-pin-line text-gold"></i>
                    <span>{workshop.location}</span>
                  </div>
                </div>

                {/* Spots & CTA */}
                <div className="flex items-center justify-between pt-6 border-t border-gold/10">
                  <span className="text-gold text-sm font-semibold">{workshop.spots}</span>
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-2 text-cream hover:text-gold transition-colors duration-300 text-sm font-semibold whitespace-nowrap"
                  >
                    Reserve Spot
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </div>

              {/* Decorative Element */}
              <div className="h-2 bg-gradient-to-r from-gold/50 via-gold to-gold/50 group-hover:from-gold group-hover:to-gold transition-all duration-500"></div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center bg-zinc-900/50 border border-gold/20 rounded-2xl p-12"
        >
          <h3 className="text-3xl font-bold text-white mb-4">
            Private Workshop Requests
          </h3>
          <p className="text-cream/70 mb-8 max-w-2xl mx-auto">
            Looking for a customized workshop for your organization? We offer bespoke problem management 
            training programs tailored to your team's specific awareness needs.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-3 bg-gold text-black px-8 py-4 rounded-full text-base font-bold hover:bg-white transition-all duration-300 whitespace-nowrap"
          >
            Request Private Workshop
            <i className="ri-arrow-right-line text-xl"></i>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default WorkshopCalendar;